import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { memo, useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import Slider from '@dtvgo/slider';
import { getNormalizedPercentageFromValue } from '@dtvgo/utils';
import { ProgressBarProvider } from './ProgressBarContext.js';
import { ScrubbingDirection, isEnterKey, isEscapeKey, isLeftArrowKey, isRightArrowKey, isValidScrubbing, ProgressBarClassNames, noop, defaultOnKeyDown, SCRUBBING_MAX_SPEED, SECONDS_TO_SCRUB_MAX_SPEED, SECONDS_TO_SCRUB, SCRUB_BASE_TIME } from './utils.js';
import './ProgressBar.scss';
import { jsx } from 'react/jsx-runtime';
import '@dtvgo/classnames';

function ProgressBar(_ref) {
  var _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$progress = _ref.progress,
    progress = _ref$progress === void 0 ? 0 : _ref$progress,
    _ref$duration = _ref.duration,
    duration = _ref$duration === void 0 ? 0 : _ref$duration,
    _ref$seekable = _ref.seekable,
    seekable = _ref$seekable === void 0 ? undefined : _ref$seekable,
    _ref$scrubbing = _ref.scrubbing,
    scrubbing = _ref$scrubbing === void 0 ? undefined : _ref$scrubbing,
    seeking = _ref.seeking,
    _ref$hideMouseOver = _ref.hideMouseOver,
    hideMouseOver = _ref$hideMouseOver === void 0 ? false : _ref$hideMouseOver,
    _ref$flat = _ref.flat,
    flat = _ref$flat === void 0 ? false : _ref$flat,
    _ref$focused = _ref.focused,
    focused = _ref$focused === void 0 ? false : _ref$focused,
    _ref$onSeek = _ref.onSeek,
    onSeek = _ref$onSeek === void 0 ? noop : _ref$onSeek,
    _ref$onSeekingStart = _ref.onSeekingStart,
    onSeekingStart = _ref$onSeekingStart === void 0 ? noop : _ref$onSeekingStart,
    _ref$onSeekingEnd = _ref.onSeekingEnd,
    onSeekingEnd = _ref$onSeekingEnd === void 0 ? noop : _ref$onSeekingEnd,
    _ref$onSeekableStartR = _ref.onSeekableStartReached,
    onSeekableStartReached = _ref$onSeekableStartR === void 0 ? noop : _ref$onSeekableStartR,
    _ref$onSeekableEndRea = _ref.onSeekableEndReached,
    onSeekableEndReached = _ref$onSeekableEndRea === void 0 ? noop : _ref$onSeekableEndRea,
    _ref$onKeyDown = _ref.onKeyDown,
    onKeyDown = _ref$onKeyDown === void 0 ? defaultOnKeyDown : _ref$onKeyDown,
    _ref$children = _ref.children,
    children = _ref$children === void 0 ? null : _ref$children;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    seekingValue = _useState2[0],
    setSeekingValue = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    mouseOverProgress = _useState4[0],
    setMouseOverProgress = _useState4[1];

  /* References to use in callbacks */
  var references = useRef({
    duration: duration,
    seeking: seeking,
    seekingValue: seekingValue,
    seekable: {
      start: (seekable === null || seekable === void 0 ? void 0 : seekable.start) || 0,
      end: (seekable === null || seekable === void 0 ? void 0 : seekable.end) || duration
    },
    scrubbing: {
      speed: scrubbing === null || scrubbing === void 0 ? void 0 : scrubbing.speed,
      direction: scrubbing === null || scrubbing === void 0 ? void 0 : scrubbing.direction
    },
    progress: progress,
    animation: {
      rafId: undefined,
      previousTime: undefined
    }
  });
  var normalizeProgressValue = useCallback(function (newProgress) {
    if (newProgress < references.current.seekable.start) {
      return references.current.seekable.start;
    }
    if (newProgress > references.current.seekable.end) {
      return references.current.seekable.end;
    }
    return newProgress;
  }, []);
  var handleMouseOver = useCallback(function (value) {
    var newProgress = normalizeProgressValue(value);
    setMouseOverProgress(newProgress);
  }, [normalizeProgressValue, setMouseOverProgress]);
  var handleSetValue = useCallback(function (newProgress) {
    // Avoid setting seeking value when seek is already in progress
    if (references.current.seeking) return;
    if (newProgress <= references.current.seekable.start) {
      onSeekableStartReached();
    }
    var normalizedProgress = normalizeProgressValue(newProgress);
    if (references.current.seekingValue === null) onSeekingStart();
    setSeekingValue(normalizedProgress);
  }, [normalizeProgressValue, onSeekableStartReached, onSeekingStart]);
  var handleDragEnd = useCallback(function (newProgress) {
    // Avoid seek when seek is already in progress
    if (references.current.seeking) return;
    var normalizedProgress = normalizeProgressValue(newProgress);
    onSeek(normalizedProgress);
  }, [normalizeProgressValue, onSeek]);
  var handleMouseLeave = useCallback(function () {
    setMouseOverProgress(null);
  }, []);
  var getProgressPercentage = useCallback(function (progressValue) {
    return getNormalizedPercentageFromValue(progressValue, references.current.duration, 0);
  }, []);
  var addSeekingValue = useCallback(function (diff) {
    if (references.current.seekingValue === null) onSeekingStart();
    setSeekingValue(function (current) {
      var value = (current !== null && current !== void 0 ? current : references.current.progress) + diff;
      return normalizeProgressValue(value);
    });
  }, [normalizeProgressValue, onSeekingStart]);
  var shouldAllowScrubbing = useCallback(function (direction) {
    var _references$current$s2;
    if (direction === ScrubbingDirection.BACKWARD) {
      var _references$current$s;
      if (((_references$current$s = references.current.seekingValue) !== null && _references$current$s !== void 0 ? _references$current$s : references.current.progress) <= references.current.seekable.start) {
        onSeekableStartReached();
        return false;
      }
    } else if (direction === ScrubbingDirection.FORWARD && ((_references$current$s2 = references.current.seekingValue) !== null && _references$current$s2 !== void 0 ? _references$current$s2 : references.current.progress) >= references.current.seekable.end) {
      onSeekableEndReached();
      return false;
    }
    return true;
  }, [onSeekableEndReached, onSeekableStartReached]);
  var startScrubbing = useCallback(function () {
    var animateSeekingValue = function animateSeekingValue(time) {
      if (!isValidScrubbing(references.current.scrubbing) || !shouldAllowScrubbing(references.current.scrubbing.direction)) return;
      if (references.current.animation.previousTime) {
        var delta = time - references.current.animation.previousTime;
        var deltaInSecs = delta / 1000;
        var secondsToScrub = references.current.scrubbing.speed === SCRUBBING_MAX_SPEED ? SECONDS_TO_SCRUB_MAX_SPEED : SECONDS_TO_SCRUB;
        var timeForSeconds = Math.pow(SCRUB_BASE_TIME, references.current.scrubbing.speed);
        var progressDiff = deltaInSecs * secondsToScrub * timeForSeconds;
        var progressDiffDirection = references.current.scrubbing.direction === ScrubbingDirection.BACKWARD ? -progressDiff : progressDiff;
        addSeekingValue(progressDiffDirection);
      }
      references.current.animation.previousTime = time;
      references.current.animation.rafId = requestAnimationFrame(animateSeekingValue);
    };
    if (references.current.animation.rafId) {
      cancelAnimationFrame(references.current.animation.rafId);
    }
    references.current.animation.rafId = requestAnimationFrame(animateSeekingValue);
  }, [addSeekingValue, shouldAllowScrubbing]);
  var stopScrubbing = useCallback(function () {
    if (references.current.animation.rafId) {
      cancelAnimationFrame(references.current.animation.rafId);
      references.current.animation.previousTime = undefined;
      references.current.animation.rafId = undefined;
    }
  }, []);
  var handleKeyDown = useCallback(function (event) {
    var keyOrContinue = onKeyDown(event);
    if (keyOrContinue === false) return;
    if (references.current.seeking) return;
    if (isEnterKey(keyOrContinue)) {
      stopScrubbing();
      onSeek(references.current.seekingValue);
    } else if (isEscapeKey(keyOrContinue)) {
      stopScrubbing();
      setSeekingValue(null);
      onSeekingEnd();
    } else if (isLeftArrowKey(keyOrContinue) || isRightArrowKey(keyOrContinue)) {
      var diff;
      if (isLeftArrowKey(keyOrContinue)) {
        if (shouldAllowScrubbing(ScrubbingDirection.BACKWARD)) {
          diff = -SECONDS_TO_SCRUB;
        }
      } else if (shouldAllowScrubbing(ScrubbingDirection.FORWARD)) {
        diff = SECONDS_TO_SCRUB;
      }
      if (diff) addSeekingValue(diff);
    }
  }, [addSeekingValue, onKeyDown, onSeek, onSeekingEnd, shouldAllowScrubbing, stopScrubbing]);
  var actions = useMemo(function () {
    return {
      normalizeProgressValue: normalizeProgressValue,
      getProgressPercentage: getProgressPercentage
    };
  }, [getProgressPercentage, normalizeProgressValue]);

  /* Update references */
  useLayoutEffect(function () {
    references.current.seekable.start = (seekable === null || seekable === void 0 ? void 0 : seekable.start) || 0;
  }, [seekable === null || seekable === void 0 ? void 0 : seekable.start]);
  useLayoutEffect(function () {
    references.current.seekable.end = (seekable === null || seekable === void 0 ? void 0 : seekable.end) || duration;
  }, [duration, seekable === null || seekable === void 0 ? void 0 : seekable.end]);
  useLayoutEffect(function () {
    references.current.duration = duration;
  }, [duration]);
  useLayoutEffect(function () {
    references.current.seekingValue = seekingValue;
  }, [seekingValue]);
  useLayoutEffect(function () {
    references.current.progress = progress;
  }, [progress]);

  /* When seeking changed, reset seekingValue and update reference */
  useLayoutEffect(function () {
    if (!seeking && references.current.seeking) {
      setSeekingValue(null);
      onSeekingEnd();
    }
    references.current.seeking = seeking;
  }, [onSeekingEnd, seeking]);

  /* When scrubbing changed, update references and start or stop scrubbing */
  useLayoutEffect(function () {
    references.current.scrubbing.direction = scrubbing === null || scrubbing === void 0 ? void 0 : scrubbing.direction;
    references.current.scrubbing.speed = scrubbing === null || scrubbing === void 0 ? void 0 : scrubbing.speed;
    if (references.current.seeking) return;
    if (isValidScrubbing(scrubbing) && shouldAllowScrubbing(scrubbing === null || scrubbing === void 0 ? void 0 : scrubbing.direction)) {
      startScrubbing();
    } else stopScrubbing();
  }, [scrubbing, shouldAllowScrubbing, startScrubbing, stopScrubbing]);
  return /*#__PURE__*/jsx(Slider, {
    className: ProgressBarClassNames.block({
      'mouse-over-hidden': hideMouseOver
    }, className),
    value: progress,
    minValue: 0,
    maxValue: duration,
    flat: flat,
    focused: focused,
    onSetValue: handleSetValue,
    onMouseOver: !hideMouseOver ? handleMouseOver : undefined,
    onMouseLeave: handleMouseLeave,
    onDragEnd: handleDragEnd,
    onKeyDown: handleKeyDown,
    children: useMemo(function () {
      return /*#__PURE__*/jsx(ProgressBarProvider, {
        mouseOverProgress: mouseOverProgress,
        seekingValue: seekingValue,
        seekable: seekable,
        duration: duration,
        scrubbing: scrubbing,
        actions: actions,
        children: children
      });
    }, [actions, children, duration, mouseOverProgress, scrubbing, seekable, seekingValue])
  });
}
var ProgressBar$1 = /*#__PURE__*/memo(ProgressBar);

export { ProgressBar$1 as default };
//# sourceMappingURL=ProgressBar.js.map
